import { useField } from 'formik';
import { useRef } from 'react';
import { KInput } from '../../atoms/KInput';
import { currencyFormatterHelpers } from '@/utils/currencyFormatter';
import {
  handleNumberInputChange,
  handleNumberPaste,
} from '@/components/formBuilder/formFields/InputFields/numberFieldValidation';

const { formatCurrencyNumber, cleanCurrencyInput } = currencyFormatterHelpers;

// KInputField doesn't have a tooltip surface; the validation helpers expect
// a tooltip trigger so we hand them a no-op (errors still surface via Yup).
const noopTooltip = () => {};

// Inline keydown gate for KInputField numeric fields — accepts digits, ',',
// '.' and lets navigation/control keys (Arrow*, Backspace, modifiers, Enter)
// fall through. We intentionally don't reuse the shared handleNumberKeyDown
// here: that helper hijacks ArrowUp/ArrowDown for value stepping, which would
// be a behavior change for the many text-typed KInputField consumers that
// rely on arrow keys for cursor navigation.
const NUMBER_ALLOWED_CHARS = /^[\d,.]$/;
const isNumberKeyAllowed = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (e.ctrlKey || e.metaKey || e.altKey) return true;
  const k = e.key;
  if (k.length !== 1) return true; // Arrow*, Backspace, Tab, Enter, etc.
  return NUMBER_ALLOWED_CHARS.test(k);
};

interface InputFieldProps {
  item?: {
    name?: string;
    inputType?: string;
    placeholder?: string;
    label?: string;
    maxLength?: number;
    disabled?: boolean;
    CustomLeftElement?: React.ElementType;
    CustomRightElement?: React.ElementType;
    inputRef?: React.RefObject<HTMLInputElement>;
    showErrorStyles?: boolean;
    isRequired?: boolean;
    formatType?: string;
    currencyFieldName?: string; // New prop to link to currency field
    currencies?: Array<{ id: number; currency: string }>; // Currencies data
    minValue?: number;
    maxValue?: number;
    stepValue?: number;
    isInputSubmit?: boolean;
    customValidation?: (
      _value: string,
      _event: React.ChangeEvent<HTMLInputElement>,
      _onChange: (_e: React.ChangeEvent<HTMLInputElement>) => void
    ) => void;
    AfterInputGroupContent?: React.ElementType;
    customOnChange?: (_event: React.ChangeEvent<HTMLInputElement>) => void;
    customOnKeyDown?: (_event: React.KeyboardEvent<HTMLInputElement>) => void;
    beforeMaxLength?: boolean;
    afterMaxLength?: boolean;
    debounceOnType?: (_value: string) => void;
    autoFocus?: boolean;
    customLeftElementProps?: any;
    customRightElementProps?: any;
    customMaxLengthProps?: any;
    fieldType?: string;
    style?: Record<string, string>;
    inputStyle?: Record<string, any>;
    onPaste?: (
      _e: React.ClipboardEvent<HTMLInputElement>,
      _setValue: (_v: string) => void
    ) => void;
    inputHeight?: string | number;
  };
  field?: any;
  formik?: any;
}

export const KInputField = ({ item = {}, field, formik }: InputFieldProps) => {
  const {
    name,
    inputType,
    placeholder = '',
    label,
    maxLength,
    CustomRightElement,
    CustomLeftElement,
    customLeftElementProps,
    customRightElementProps,
    inputRef,
    // showErrorStyles = false,
    isRequired,
    formatType,
    currencyFieldName, // New prop to link to currency field
    minValue = 0,
    maxValue,
    stepValue,
    isInputSubmit = false,
    customValidation,
    AfterInputGroupContent,
    customOnChange = () => {},
    customOnKeyDown,
    // beforeMaxLength,
    customMaxLengthProps,
    // afterMaxLength = true,
    debounceOnType = () => {},
    autoFocus = false,
    disabled = false,
    fieldType,
    style,
    inputStyle,
    onPaste,
    inputHeight,
  } = item;

  const [_field, meta, helpers] = useField(name);
  const { setTouched, setValue } = helpers;
  const { submitCount } = formik || {};
  const { touched } = meta || {};

  // Get currency field value if currencyFieldName is provided
  const [, currencyMeta] = useField(
    currencyFieldName || 'dummy_currency_field'
  );
  const selectedCurrencyId = currencyFieldName ? currencyMeta?.value : null;

  const intervalRef = useRef(null);
  const showError = (submitCount > 0 || touched) && meta?.error && isRequired;
  const localInputRef = useRef<HTMLInputElement>(null);

  // Treat a field as numeric if either signal is set — config across the
  // codebase uses both inputType='number' (filter/form configs) and
  // fieldType='number' (offer form inline fields).
  const isNumberField = inputType === 'number' || fieldType === 'number';

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (isNumberField && handleNumberPaste(e, noopTooltip)) {
      // Paste rejected by validator — don't fall through to onPaste.
      return;
    }
    if (onPaste) onPaste(e, setValue);
  };

  // Return null if name is not provided as it's required for useField
  if (!item || !name) return null;

  // Function to format a number as currency as thousand separators
  const formatNumberWithCommas = (value) => {
    if (!value) return '';

    const numericValue = (value?.toString() || '')
      .split('.')[0]
      .replace(/[^\d]/g, '');

    // Format the number with commas
    return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Get the selected currency string from the currencies data
  const getSelectedCurrencyString = () => {
    if (!currencyFieldName || !selectedCurrencyId || !item.currencies)
      return null;

    // Find the selected currency in the currencies array
    const selectedCurrency = item.currencies.find(
      (c) => c.id === selectedCurrencyId
    );
    return selectedCurrency?.currency || null;
  };

  // Format the value based on formatType and selected currency
  const formattedValue = (() => {
    if (formatType === 'currency') {
      const currencyString = getSelectedCurrencyString();
      if (currencyString && currencyFieldName) {
        return formatCurrencyNumber(field?.value || '', currencyString);
      }
      // Fallback to comma formatting if no currency is selected
      return formatNumberWithCommas(field?.value || '');
    }
    return field?.value || '';
  })();

  const handleOnChange = (event) => {
    clearTimeout(intervalRef.current);
    const value = event.target.value;
    customOnChange(event);

    intervalRef.current = setTimeout(() => {
      debounceOnType(value);
    }, 1000);

    if (customValidation) {
      customValidation(value, event, field.onChange);
    }
    // Number fields: delegate to the shared validation helper so KInputField
    // and the legacy InputField stay in lockstep on separator handling,
    // safe-integer preservation, leading-zero rules, etc.
    else if (isNumberField) {
      handleNumberInputChange(value, setValue, noopTooltip);
    } else if (formatType !== 'currency') {
      field.onChange(event);
    } else {
      // For currency fields, clean the input and store only numeric value
      const cleanedValue = cleanCurrencyInput(value);
      setValue(cleanedValue);
    }
  };

  return (
    <>
      <KInput
        {...field}
        onChange={handleOnChange}
        onClick={() => {
          if (inputRef?.current) {
            inputRef.current?.focus();
          } else if (localInputRef?.current) {
            localInputRef.current?.focus();
          }
        }}
        ref={inputRef || localInputRef}
        id={name}
        autoComplete="off"
        type={
          formatType === 'currency' || isNumberField
            ? 'text'
            : inputType || 'text'
        }
        inputMode={
          (formatType === 'currency' && fieldType === 'number') || isNumberField
            ? 'decimal'
            : undefined
        }
        value={formattedValue}
        placeholder={
          placeholder || `Enter ${label ? label?.toLowerCase() : ''}`
        }
        maxLength={maxLength}
        min={minValue}
        max={maxValue}
        step={stepValue}
        error={showError}
        onFocus={() => setTouched(false)}
        onBlur={() => setTouched(true)}
        disabled={disabled}
        onKeyDown={(e) => {
          if (customOnKeyDown) {
            customOnKeyDown(e);
          }
          if (isNumberField) {
            if (!isNumberKeyAllowed(e)) {
              e.preventDefault();
            } else if (e.key === '0') {
              // Match InputField behavior — block leading zero (e.g. "012")
              // so the change-handler's leading-zero rejection isn't reached
              // mid-typing.
              const input = e.currentTarget;
              if (input.selectionStart === 0 && input.value !== '') {
                e.preventDefault();
              }
            }
          }
          if (e.key === 'Enter') {
            e.preventDefault(); // Prevent the default form submission
            if (formik && isInputSubmit) {
              formik.submitForm(); // Manually submit the form
            }
          }
        }}
        style={style}
        inputStyle={inputStyle}
        autoFocus={autoFocus}
        onPaste={handlePaste}
        CustomRightElement={CustomRightElement}
        AfterInputGroupContent={AfterInputGroupContent}
        CustomLeftElement={CustomLeftElement}
        customLeftElementProps={customLeftElementProps}
        customRightElementProps={customRightElementProps}
        customMaxLengthProps={customMaxLengthProps}
        fieldType={
          formatType === 'currency' || isNumberField ? undefined : fieldType
        }
        inputHeight={inputHeight}
      />
      {/* these are already handled in the input field */}

      {/* {CustomRightElement && beforeMaxLength && <CustomRightElement />} */}
      {/* {MaxLengthElement && <MaxLengthElement />} */}
      {/* {CustomRightElement && afterMaxLength && !beforeMaxLength && (
        <CustomRightElement />
      )} */}
    </>
  );
};
